import type { ReactNode } from 'react';
import { getDepthStyle } from './FileContextMenu';
import type { Node } from '~/types/file-tree';
import { classNames } from '~/utils/classNames';

interface Props {
  node: Node;
  selected?: boolean;
  collapsed?: boolean;
  isSelectedAsCut?: boolean;
  onClick: () => void;
  provideQuickActions?: (node: Node) => ReactNode;
  renderContent?: (node: Node) => ReactNode;
  parentPath?: string;
}

export function FileOrFolder({
  node,
  selected,
  collapsed,
  isSelectedAsCut,
  onClick,
  provideQuickActions,
  renderContent,
  parentPath,
}: Props) {
  return (
    <button
      className={classNames(
        'group flex items-center gap-1.5 w-full pr-2 border-2 border-transparent text-faded py-0.5',
        {
          'bg-transparent text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive hover:bg-bolt-elements-item-backgroundActive':
            !selected,
          'bg-bolt-elements-item-backgroundAccent text-bolt-elements-item-contentAccent': selected,
          'hover:text-bolt-elements-item-contentDefault': isSelectedAsCut && !selected,
        },
      )}
      onClick={onClick}
      style={getDepthStyle(node.depth)}
      aria-pressed={node.kind === 'folder' ? !collapsed : selected}
      data-parent-path={parentPath}
    >
      <div
        className={classNames('shrink-0 scale-98', {
          'i-ph:file-duotone': node.kind === 'file',
          'group-hover:text-bolt-elements-item-contentActive': node.kind === 'file' && !selected && !isSelectedAsCut,
          'i-ph:caret-right': node.kind === 'folder' && collapsed,
          'i-ph:caret-down': node.kind === 'folder' && !collapsed,
          'opacity-50 ': isSelectedAsCut,
        })}
      ></div>
      <div className="truncate w-full text-left">
        <div
          className={classNames('flex items-center', {
            'group-hover:text-bolt-elements-item-contentActive': !selected && !isSelectedAsCut,
          })}
        >
          {renderContent ? (
            renderContent(node)
          ) : (
            <>
              <div translate="no" className={classNames('flex-1 truncate pr-2', { 'opacity-50 ': isSelectedAsCut })}>
                {node.name}
              </div>
              {provideQuickActions?.(node)}
            </>
          )}
        </div>
      </div>
    </button>
  );
}
